/**
 * FES2014 offline tidal prediction — heights AND currents from harmonic
 * constituents, computed in the browser with no network and no per-user key.
 *
 * HOW IT FITS: FES2014 (CNES/AVISO, free, open) is a global tidal atlas of
 * harmonic constituents (amplitude + Greenwich phase lag) on a grid. The heavy
 * netCDF files are read ONCE, offline, by scripts/extract_fes.py, which pulls the
 * constituents at a single location (e.g. Tobermory) and writes a tiny JSON. This
 * module is the browser-side predictor that sums those constituents for any time.
 * Same split as the chart converter: big data in Python, small JSON in the app.
 *
 * FIDELITY: this is the free, COARSE indication. FES is a ~1/16° (~7 km) model,
 * so in a narrow sea loch like the Sound of Mull the currents in particular are
 * indicative, not navigational. Heights are good. The equilibrium argument here
 * uses the standard astronomical longitudes and Doodson combinations with nodal
 * corrections on the principal constituents; the constituent phase constants
 * follow the Schureman/IHO convention FES phases are referenced to. Validate
 * against a known Tobermory prediction once the FES data is in — the diurnal
 * sign conventions are the thing to check. For accurate streams, the authored
 * tidal-diamond slot (real Sound of Mull diamonds) is the upgrade path.
 */

/** Angular speed (deg/hour) of each supported constituent. */
const SPEED: Record<string, number> = {
  M2: 28.9841042, S2: 30.0, N2: 28.4397295, K2: 30.0821373,
  K1: 15.0410686, O1: 13.9430356, P1: 14.9589314, Q1: 13.3986609,
  M4: 57.9682084, MS4: 58.9841042, MN4: 57.4238337,
  MF: 1.0980331, MM: 0.5443747, SSA: 0.0821373,
};

/** Doodson coefficients [tau, s, h, p, N', p1] and the constant phase (deg). */
const DOODSON: Record<string, { c: [number, number, number, number, number, number]; phase: number }> = {
  M2: { c: [2, 0, 0, 0, 0, 0], phase: 0 },
  S2: { c: [2, 2, -2, 0, 0, 0], phase: 0 },
  N2: { c: [2, -1, 0, 1, 0, 0], phase: 0 },
  K2: { c: [2, 2, 0, 0, 0, 0], phase: 0 },
  K1: { c: [1, 1, 0, 0, 0, 0], phase: 90 },
  O1: { c: [1, -1, 0, 0, 0, 0], phase: -90 },
  P1: { c: [1, 1, -2, 0, 0, 0], phase: -90 },
  Q1: { c: [1, -2, 0, 1, 0, 0], phase: -90 },
  M4: { c: [4, 0, 0, 0, 0, 0], phase: 0 },
  MS4: { c: [4, 2, -2, 0, 0, 0], phase: 0 },
  MN4: { c: [4, -1, 0, 1, 0, 0], phase: 0 },
  MF: { c: [0, 2, 0, 0, 0, 0], phase: 0 },
  MM: { c: [0, 1, 0, -1, 0, 0], phase: 0 },
  SSA: { c: [0, 0, 2, 0, 0, 0], phase: 0 },
};

const D2R = Math.PI / 180;
const norm360 = (x: number) => ((x % 360) + 360) % 360;

/** Fundamental astronomical longitudes (deg) + lunar time tau at a UTC instant. */
function astro(ms: number): { tau: number; s: number; h: number; p: number; N: number; p1: number } {
  // Julian centuries from J2000.0 (JD 2451545.0).
  const jd = ms / 86_400_000 + 2440587.5;
  const T = (jd - 2451545.0) / 36525;
  const s = norm360(218.3164477 + 481267.88123421 * T);
  const h = norm360(280.4664567 + 36000.76982779 * T);
  const p = norm360(83.3532465 + 4069.0137287 * T);
  const N = norm360(125.0445479 - 1934.1362891 * T);
  const p1 = norm360(282.9373409 + 1.7195877 * T);
  // Mean lunar time: 15deg * UT hours + h - s.
  const utHours = (ms / 3_600_000) % 24;
  const tau = norm360(15 * utHours + h - s);
  return { tau, s, h, p, N, p1 };
}

/** Nodal amplitude factor f and phase correction u (deg) for the principal terms. */
function nodal(name: string, Ndeg: number): { f: number; u: number } {
  const N = Ndeg * D2R;
  switch (name) {
    case 'M2':
    case 'N2':
    case 'MN4':
      return { f: 1 - 0.037 * Math.cos(N), u: -2.1 * Math.sin(N) };
    case 'M4':
      return { f: (1 - 0.037 * Math.cos(N)) ** 2, u: -4.2 * Math.sin(N) };
    case 'MS4':
      return { f: 1 - 0.037 * Math.cos(N), u: -2.1 * Math.sin(N) };
    case 'O1':
    case 'Q1':
      return { f: 1.009 + 0.187 * Math.cos(N), u: 10.8 * Math.sin(N) };
    case 'K1':
      return { f: 1.006 + 0.115 * Math.cos(N), u: -8.9 * Math.sin(N) };
    case 'K2':
      return { f: 1.024 + 0.286 * Math.cos(N), u: -17.7 * Math.sin(N) };
    case 'MF':
      return { f: 1.043 + 0.414 * Math.cos(N), u: -23.7 * Math.sin(N) };
    default:
      return { f: 1, u: 0 };
  }
}

/** The equilibrium argument V (deg) of a constituent at an instant. */
function equilibrium(name: string, a: ReturnType<typeof astro>): number {
  const d = DOODSON[name];
  if (!d) return 0;
  const [t, s, h, p, N, p1] = d.c;
  return norm360(t * a.tau + s * a.s + h * a.h + p * a.p + N * a.N + p1 * a.p1 + d.phase);
}

/** One height constituent at a station: amplitude (m) + Greenwich phase lag (deg). */
export interface FesHeightConst {
  name: string;
  ampM: number;
  phaseDeg: number;
}
/** One current constituent: east/north amplitude (kn) + phase (deg) for each. */
export interface FesCurrentConst {
  name: string;
  uAmpKn: number;
  uPhaseDeg: number;
  vAmpKn: number;
  vPhaseDeg: number;
}

export interface FesStation {
  /** Stable id, e.g. 'tobermory'. */
  id: string;
  name: string;
  position: [number, number];
  /** Mean sea level offset above chart datum, metres (Z0). */
  datumOffsetM: number;
  height: FesHeightConst[];
  current?: FesCurrentConst[];
}

/** Predicted height (m above chart datum) at a station and instant. */
export function predictHeightM(st: FesStation, ms: number): number {
  const a = astro(ms);
  let sum = st.datumOffsetM;
  for (const c of st.height) {
    const spd = SPEED[c.name];
    if (spd == null) continue;
    const { f, u } = nodal(c.name, a.N);
    const arg = (equilibrium(c.name, a) + u - c.phaseDeg) * D2R;
    sum += f * c.ampM * Math.cos(arg);
  }
  return sum;
}

/** Predicted current (set deg true, rate kn) at a station and instant. */
export function predictCurrent(st: FesStation, ms: number): { setDeg: number; rateKn: number } {
  if (!st.current || st.current.length === 0) return { setDeg: 0, rateKn: 0 };
  const a = astro(ms);
  let east = 0;
  let north = 0;
  for (const c of st.current) {
    const spd = SPEED[c.name];
    if (spd == null) continue;
    const { f, u } = nodal(c.name, a.N);
    const V = equilibrium(c.name, a) + u;
    east += f * c.uAmpKn * Math.cos((V - c.uPhaseDeg) * D2R);
    north += f * c.vAmpKn * Math.cos((V - c.vPhaseDeg) * D2R);
  }
  const rateKn = Math.hypot(east, north);
  // Set is the direction the flow goes TOWARDS, degrees true (north=0, east=90).
  const setDeg = norm360((Math.atan2(east, north) * 180) / Math.PI);
  return { setDeg, rateKn };
}
